import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { CTAButtons } from "@/components/site/CTAButtons";
import supplies from "@/assets/cleaning-supplies.webp";
import { Shield, Heart, MessageCircle, Sparkles } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About IZHUB Cleaning Service: Trusted Local Cleaners" },
      { name: "description", content: "IZHUB Cleaning Service is a friendly, detail-focused local cleaning team serving Tidworth, Andover, Salisbury and Salisbury Plain." },
      { property: "og:title", content: "About IZHUB Cleaning Service: Trusted Local Cleaners" },
      { property: "og:description", content: "A friendly, detail-focused local cleaning team serving Tidworth, Andover, Salisbury and Salisbury Plain." },
      { property: "og:url", content: "https://izhubcleaning.uk/about" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/about" }],
  }),
  component: About,
});

const values = [
  { icon: Shield, title: "Trust first", text: "Letting a cleaner into your home is a big deal. We take that seriously, every visit, every property." },
  { icon: Sparkles, title: "Detail-focused", text: "Skirting boards, edges, cupboard interiors, the little things that separate a good clean from a great one." },
  { icon: MessageCircle, title: "Friendly & clear", text: "Straight answers, clear pricing, and easy communication before, during and after every clean." },
  { icon: Heart, title: "Consistently high", text: "The same high standard on every visit, not just the first one." },
];

function About() {
  return (
    <>
      <PageHero
        title={<>Professional cleaning that <span className="rainbow-text">earns your trust</span>.</>}
        intro="IZHUB Cleaning Service is a local, professional cleaning team serving Tidworth, Andover, Salisbury and the wider Salisbury Plain area. We work with homeowners, tenants, landlords, letting agents, Airbnb hosts, small businesses and military families."
        withBackground
      />

      <section className="py-20 md:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <img src={supplies} alt="Neatly arranged cleaning supplies" loading="lazy" className="rounded-3xl w-full h-auto object-cover aspect-square shadow-soft" />
          <div className="text-surface">
            <span className="eyebrow">Our approach</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl">Reliable, thorough, and easy to work with.</h2>
            <div className="mt-6 space-y-4 text-muted-foreground">
              <p>We believe cleaning isn't just about the visible surfaces. It's about the parts that get missed: the corners, the edges, the details that make a home feel truly cared for.</p>
              <p>Whether we're helping a family stay on top of a busy household, preparing a rental for inspection, or getting a service family through their march-out, our promise is the same: reliable people, consistent standards, and clear communication.</p>
              <p>We're insured, punctual, and local, and we'd love the chance to help you next.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream/85 backdrop-blur-sm border-y border-border">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="eyebrow">What we stand for</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl">Values that guide every visit.</h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-3xl border border-border bg-card p-6">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-sage-soft text-primary"><v.icon className="h-5 w-5" /></span>
                <h3 className="mt-5 font-display text-lg">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-x">
          <div className="rounded-3xl bg-primary text-primary-foreground p-8 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl text-primary-foreground max-w-2xl mx-auto">Let's make your next clean the easy one.</h2>
            <p className="mt-4 text-primary-foreground/85 max-w-lg mx-auto">Free, no-obligation quote, usually back within 2 hours.</p>
            <div className="mt-8 flex justify-center"><CTAButtons /></div>
          </div>
        </div>
      </section>
    </>
  );
}
